import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button.js';
import { cn } from '@/lib/utils.js';

import type { HintNode } from './hint-config.js';

export interface HintContentProps {
  hint: HintNode;
  /** Extra classes on the clip — a host with less vertical room passes its own `max-h` cap here. */
  videoClassName?: string;
}

/**
 * Shared body for hint dialogs and cards. Renders by `mode`, and when a video fails to load
 * falls back to the text body so the surface never goes blank. Clips always try to autoplay
 * with sound. If the browser blocks unmuted autoplay, the clip stays paused with a play
 * affordance — never continues muted in the background.
 */
export function HintContent({
  hint,
  videoClassName,
}: HintContentProps): ReactNode {
  const [videoFailed, setVideoFailed] = useState(false);
  const [needsPlayGesture, setNeedsPlayGesture] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const showVideo =
    (hint.mode === 'text+video' || hint.mode === 'video') &&
    Boolean(hint.videoSrc) &&
    !videoFailed;

  const showText =
    hint.mode === 'text' ||
    hint.mode === 'text+video' ||
    (hint.mode === 'video' && (!hint.videoSrc || videoFailed));

  const videoAspect = hint.videoAspect ?? 'square';

  useEffect(() => {
    if (!showVideo) return;
    const video = videoRef.current;
    if (!video) return;

    setNeedsPlayGesture(false);
    video.defaultMuted = false;
    video.muted = false;
    video.volume = 1;

    const playAttempt = video.play();
    if (!playAttempt) return;

    void playAttempt.catch(() => {
      video.pause();
      video.currentTime = 0;
      video.muted = false;
      setNeedsPlayGesture(true);
    });
  }, [hint.videoSrc, showVideo]);

  /** Starts playback with sound after a user gesture (required when autoplay is blocked). */
  function handlePlayWithSound(): void {
    const video = videoRef.current;
    if (!video) return;
    video.defaultMuted = false;
    video.muted = false;
    video.volume = 1;
    setNeedsPlayGesture(false);
    void video.play();
  }

  return (
    <div className="space-y-3">
      {showVideo ? (
        <div className="relative mx-auto w-full">
          <video
            ref={videoRef}
            data-testid="hint-video"
            data-aspect={videoAspect}
            autoPlay
            playsInline
            controls
            className={cn(
              'mx-auto block rounded object-contain',
              videoAspect === 'wide'
                ? 'aspect-video w-full max-h-[min(45vh,28rem)]'
                : 'aspect-square w-full max-w-[min(100%,32rem)] max-h-[min(55vh,32rem)]',
              videoClassName,
            )}
            onError={() => setVideoFailed(true)}
          >
            <source src={hint.videoSrc} type="video/mp4" />
          </video>
          {needsPlayGesture ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-background/55">
              <Button
                type="button"
                data-testid="hint-video-play"
                className="hint-video-play-nudge px-4"
                onClick={handlePlayWithSound}
              >
                {hint.id === 'onboarding.intro'
                  ? 'Watch the intro'
                  : 'See how it works'}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {showText ? (
        <p className="text-sm text-muted-foreground">{hint.body}</p>
      ) : null}
    </div>
  );
}
